import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Anchor, Mountain, Cpu, ChevronRight, Lock, Sparkles,
  TrendingUp, Globe, Activity, Shield, Compass, Zap, ArrowRight,
  Droplets, Factory, Database, Wrench,
} from "lucide-react";
import axios from "axios";
import usePageview, { getVisitorSession } from "@/lib/usePageview";
import VideoExplainer from "@/components/VideoExplainer";
import VideoAd from "@/components/VideoAd";

// Phase 36.11 — Industry-specific public landing pages.
// Each route under /industries/* maps to a config block here that drives the
// hero backdrop, headline copy, feature cards, and email-capture target.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INDUSTRIES = {
  shipping: {
    label: "Shipping & Maritime",
    routeKey: "shipping",
    backdrop: "/industry-bg/shipping-command-map.png",
    accent: "#FFB000",
    Icon: Anchor,
    eyebrow: "// SHIPPING · MARITIME · LOGISTICS",
    headline: "A unified command map for every voyage.",
    sub: "Track every cargo, every demurrage clock, every charterer credit risk. BIMCO-grade compliance, OFAC sanctions screening, and DARA-grounded route economics — the operator terminal Veson, Q88, and Inchcape never built into one screen.",
    metaPills: ["BIMCO", "OFAC · EU · UN", "IMO 2020", "AIS LIVE", "PLATTS BUNKERS"],
    features: [
      {
        Icon: Globe,
        title: "Mine-to-Port Orchestration",
        body: "Real-time AIS tracking across the global fleet. Live weather routing, ETA prediction at 94% confidence, and a single command map covering every voyage in your book.",
      },
      {
        Icon: Shield,
        title: "Sanctions Screening · Hard-Fail",
        body: "Every charterer screened against OFAC SDN, EU consolidated, and UN Security Council lists on every fixture. Hard-fail on any hit. Auto-disclosed in board reports — never another unwitting designated-party fixture.",
      },
      {
        Icon: Activity,
        title: "Demurrage Exposure",
        body: "Real-time laycan adherence, port-congestion modelling, per-charter demurrage forecasting. Catches the 2-day overage before it costs you USD 80K. Bills it back before the next nomination.",
      },
      {
        Icon: TrendingUp,
        title: "Bunker Hedge Co-Pilot",
        body: "ICE Brent-anchored hedging programmes for IMO 2020 fuel mix. DARA proposes specific lots / tenors / reference prices, then DARA marks-to-market live.",
      },
    ],
    proof: "Built for capesize bulkers, container fleets, tanker pools, and inland-port terminals — where one missed laycan can cost USD 80K/day.",
    ctaLabel: "Talk to a founder",
    formHeadline: "See what's hiding in your demurrage clocks.",
    formSub: "Drop your details and a DARA founder reaches out within one business day with a tailored shipping walkthrough — fixtures, sanctions, demurrage, bunker hedge. No spam, no drip.",
    // Phase 25.13au — bundled industrial demo (factory floor → revenue lines).
    // Same video runs for mining + shipping since it covers both narratives:
    // tonnes shipped converting into live revenue lines, predictive maintenance,
    // ESG reporting. Used only when no admin override is set.
    defaultVideoUrl: "/videos/dara_industrial.mp4",
    defaultVideoLabel: "// DARA · PLUGGED INTO THE FACTORY FLOOR",
    defaultVideoCaption: "TONNES SHIPPED → REVENUE LINES · 35 SECONDS · OPC UA · MODBUS · NO NEW HARDWARE",
  },
  mining: {
    label: "Mining & Resources",
    routeKey: "mining",
    backdrop: "/industry-bg/mining-unified-terminal.png",
    accent: "#FFB000",
    Icon: Mountain,
    eyebrow: "// MINING · METALS · RESOURCES",
    headline: "The only unified terminal for pit-to-port.",
    sub: "From JORC ore reserves to Hedge Co-Pilot, from royalty completeness to live commodity-index linkage — DARA gives resource operators the treasury terminal Tableau, Power BI, and Palantir Foundry never built.",
    metaPills: ["JORC 2012", "NI 43-101", "SAMREC", "ICMM", "ASX LISTING"],
    features: [
      {
        Icon: Mountain,
        title: "JORC / NI 43-101 Documentation",
        body: "Reserve documentation completeness, grade variability (CV), and mine-call factor reconciliation — auto-checked against deposit and offtake concentration thresholds. Citation-ready for ASX, TSX, and SARB filings.",
      },
      {
        Icon: TrendingUp,
        title: "Hedge Co-Pilot",
        body: "SGX IODEX 62% Fe, LME 3M Copper, COMEX Gold. DARA-grounded programmes with specific lots, tenors, reference prices. Marked-to-market live every load — IFRS 9 / IAS 32 caveats included.",
      },
      {
        Icon: Activity,
        title: "Commodity Desk · 9 Live Indices",
        body: "Iron Ore, Copper, Gold, Coal (thermal + coking), Brent, Nickel, Aluminium, Zinc — 36-month back-history plus 12-month forecast with support / resistance bands. Slack alerts on threshold breaches.",
      },
      {
        Icon: Shield,
        title: "Royalty + Mine-Call Reconciliation",
        body: "Per-shipment royalty completeness against state acts. Grade reconciliation between assay and shipment, surfacing systematic short-pays before audit. 1% grade variance ≈ USD 4M / year on a mid-size book.",
      },
    ],
    proof: "Built for iron-ore, gold, copper, nickel, and coal operators across AU / ZA / Chile / Brazil — where 1% grade reconciliation = USD 4M / year.",
    ctaLabel: "Talk to a founder",
    formHeadline: "See what's hiding in your grade reconciliation.",
    formSub: "Drop your details and a DARA founder reaches out within one business day with a tailored mining walkthrough — JORC docs, royalty completeness, hedge programmes, mine-call reconciliation. No spam, no drip.",
    defaultVideoUrl: "/videos/dara_industrial.mp4",
    defaultVideoLabel: "// DARA · PLUGGED INTO THE FACTORY FLOOR",
    defaultVideoCaption: "PIT-TO-PORT VISIBILITY · 35 SECONDS · OPC UA · MODBUS · NO NEW HARDWARE",
  },
  "heavy-machinery": {
    label: "Heavy Machinery & Industrial",
    routeKey: "heavy-machinery",
    backdrop: "/industry-bg/heavy-machinery-founder.png",
    accent: "#FFB000",
    Icon: Cpu,
    eyebrow: "// HEAVY MACHINERY · INDUSTRIAL · IIoT",
    headline: "Three languages of machines. One operating system.",
    sub: "OPC UA. MODBUS. SCADA Historian. DARA's Industrial Adapter speaks every protocol your fleet talks — and turns telemetry into cash-flow forecasts, predictive maintenance, and Hedge Ledger volumes the next morning.",
    metaPills: ["OPC UA", "MODBUS RTU/TCP", "SCADA HISTORIAN", "EDGE → CLOUD", "TLS 1.3"],
    features: [
      {
        Icon: Cpu,
        title: "OPC UA Native",
        body: "Industrial automation backbone. Siemens S7, ABB AC500, Rockwell ControlLogix. DARA ingests tag-by-tag with namespace and data-type fidelity preserved end-to-end.",
      },
      {
        Icon: Zap,
        title: "MODBUS RTU + TCP",
        body: "Drill rigs, conveyors, crushers, pumps — legacy serial and TCP variants. Coil / discrete / holding / input register reads at configurable polling cadence (1s → 1min).",
      },
      {
        Icon: Activity,
        title: "SCADA Historian Pull",
        body: "Wonderware InTouch, OSIsoft PI, GE iFIX. Backfill 30 days of historian on first connect, then incremental sync at your chosen cadence. No process disruption.",
      },
      {
        Icon: Lock,
        title: "Edge → Cloud · Encrypted",
        body: "TLS 1.3 ingest to /api/iot/ingest. 60 batches/min/IP. Site-to-site auth with rotating tokens. Q3 2026 GA — early-access partners shape the protocol roadmap.",
      },
    ],
    proof: "Q3 2026 preview — currently being scoped with mining, oil & gas, and large-fleet operators across three continents.",
    ctaLabel: "Reserve early access",
    formHeadline: "See what your historian isn't telling you.",
    formSub: "Reserve early access and a DARA founder will scope your protocols (OPC UA · MODBUS · SCADA Historian) within one business day. Early-access partners shape the protocol roadmap and skip the Q3 2026 queue.",
  },
  water: {
    label: "Water & Utilities",
    routeKey: "water",
    backdrop: "/industry-bg/heavy-machinery-founder.png",
    accent: "#FFB000",
    Icon: Droplets,
    eyebrow: "// WATER · UTILITIES · OT-FIRST",
    headline: "An analyst on every treatment plant — without buying new SCADA.",
    sub: "Sydney Water, SA Water, Melbourne Water, Hunter Water all face the same mandate: prove a digital-twin / predictive-maintenance pathway by 2027. DARA's Industrial Connector reads your existing OPC UA / OSIsoft PI / Wonderware tag set, lays a Bloomberg-terminal analytics layer over the top, and ships DARA-grounded operational briefs weekly. No hardware. No PLC touch. Read-only.",
    metaPills: ["OPC UA", "OSIsoft PI", "WONDERWARE", "IEC 62443 L2", "AS/NZS 4360"],
    features: [
      {
        Icon: Droplets,
        title: "Plant-level Operational Briefs",
        body: "Weekly DARA Engine brief per treatment plant — pump degradation, chemical-dose drift, throughput anomalies, blower energy waste. The analyst your Manager-OT never had time to be.",
      },
      {
        Icon: Activity,
        title: "Pump + Blower Predictive Maintenance",
        body: "ISO 10816 vibration band tracking, pump-curve degradation, motor-current signature analysis. Catches the 6-week-out failure before it costs $80k in emergency repair + DERM penalty.",
      },
      {
        Icon: Database,
        title: "Read-only OPC UA / PI Ingest",
        body: "We connect to your existing historian. No new hardware. No PLC reach. No control-loop touch. The InfoSec gauntlet you've been dreading — passes on the first review.",
      },
      {
        Icon: Shield,
        title: "Regulator-Ready Reporting",
        body: "Auto-generates the OT-maturity narrative your state regulator wants by 2027. IEC 62443 alignment roadmap baked in. Compatible with NIST CSF 2.0 and AS/NZS 4360 risk frameworks.",
      },
    ],
    proof: "Built for AU water utilities running OSIsoft PI / Aveva / Wonderware — designed alongside Manager-OT teams who would rather have one weekly brief than ten dashboards. Two design-partner slots open through Q3 2026.",
    ctaLabel: "Apply as a design partner",
    formHeadline: "Two water-utility design-partner slots. 12 weeks. Zero fee.",
    formSub: "Apply with one tag-set (one treatment plant, one substation) and DARA's founder will scope the InfoSec posture, run a 30-min discovery call, and confirm fit within 5 business days. Pilot connector live within 4 weeks of signed agreement.",
  },
};

export default function IndustryLanding() {
  const { slug } = useParams();
  const cfg = useMemo(() => INDUSTRIES[slug] || INDUSTRIES.shipping, [slug]);
  usePageview(`/industries/${cfg.routeKey}`);

  const envExplainerUrl = process.env.REACT_APP_EXPLAINER_VIDEO_URL || "";
  // Phase 25.2c — admin can override the demo video URL per industry from
  // /app/admin/case-studies. Falls back to env var if no override is set.
  const [perIndustryDemo, setPerIndustryDemo] = useState({ video_url: "", headline: "" });
  React.useEffect(() => {
    const ind = (cfg.routeKey || slug || "").toLowerCase();
    if (!ind) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/industry-demos/${ind}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setPerIndustryDemo({ video_url: d.video_url || "", headline: d.headline || "" }); })
      .catch(() => {});
  }, [cfg, slug]);
  const explainerUrl = perIndustryDemo.video_url || envExplainerUrl;
  // Phase 25.13au — when neither an admin-set override nor an env-var video
  // is configured, fall back to the bundled industrial video for industries
  // that ship a default (mining + shipping currently).
  const fallbackVideoUrl = (!explainerUrl && cfg.defaultVideoUrl) ? cfg.defaultVideoUrl : null;

  const [form, setForm] = useState({ email: "", name: "", company: "", role: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Email is required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/industry-leads`, {
        ...form,
        industry: cfg.routeKey,
        visitor_session: getVisitorSession(),
      });
      setSubmitted(true);
      toast.success("You're on the list — DARA will be in touch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const Icon = cfg.Icon;

  return (
    <div className="min-h-screen bg-black text-white" data-testid={`industry-landing-${cfg.routeKey}`}>
      {/* Top nav */}
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/85 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="back-to-home">
            <span className="text-[#FFB000] font-mono text-sm">{`>_`}</span>
            <span className="font-heading font-black tracking-tight text-white text-base">DARA</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 ml-2 hidden sm:inline">
              · {cfg.label}
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white"
              data-testid="nav-signin"
            >
              Sign in
            </Link>
            <a
              href="#capture"
              data-testid="nav-cta"
              className="font-mono text-[10px] uppercase tracking-widest bg-[#FFB000] text-black px-3 py-1.5 hover:opacity-90"
            >
              {cfg.ctaLabel} →
            </a>
          </div>
        </div>
      </nav>

      {/* HERO with backdrop image */}
      <section className="relative overflow-hidden border-b border-zinc-900">
        <img
          src={cfg.backdrop}
          alt={`${cfg.label} command view`}
          className="absolute inset-0 w-full h-full object-cover opacity-50"
          data-testid="industry-backdrop"
        />
        {/* Multi-layer gradient overlay for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black" />
        {/* Faint scanline pattern */}
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,176,0,0.4) 2px, rgba(255,176,0,0.4) 3px)" }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 lg:py-32">
          <div className="max-w-2xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-4 flex items-center gap-2">
              <Icon className="w-3.5 h-3.5" />
              {cfg.eyebrow}
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              {cfg.headline}
            </h1>
            <p className="text-base sm:text-lg text-zinc-300 mt-6 leading-relaxed max-w-xl">
              {cfg.sub}
            </p>

            {/* Meta pills */}
            <div className="flex flex-wrap gap-2 mt-7" data-testid="industry-meta-pills">
              {cfg.metaPills.map(p => (
                <span
                  key={p}
                  className="px-2.5 py-1 border border-[#FFB000]/40 text-[#FFB000] bg-[#FFB000]/5 font-mono text-[10px] uppercase tracking-widest"
                >
                  {p}
                </span>
              ))}
            </div>

            <div className="mt-10 flex items-center gap-4 flex-wrap">
              <a
                href="#capture"
                data-testid="hero-cta"
                className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90"
              >
                <Compass className="w-4 h-4" /> {cfg.ctaLabel} <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/founders-guide"
                data-testid="hero-secondary-cta"
                className="font-mono text-[11px] uppercase tracking-widest text-zinc-300 hover:text-[#FFB000] underline-offset-4 hover:underline"
              >
                Read the Founder's Guide →
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom-right scanlines glow accent */}
        <div className="hidden lg:block absolute bottom-8 right-8 font-mono text-[10px] uppercase tracking-widest text-zinc-600">
          DARA OS · v2.1
        </div>
      </section>

      {/* Feature grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// CAPABILITIES</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight max-w-3xl">
          Every module a {cfg.label.split(" & ")[0].toLowerCase()} operator actually needs.
        </h2>
        <p className="text-sm text-zinc-400 max-w-2xl mt-3">{cfg.proof}</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-zinc-900 mt-10 border border-zinc-900">
          {cfg.features.map((f) => {
            const FIcon = f.Icon;
            return (
              <div key={f.title} className="bg-black p-6 sm:p-8" data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="border border-[#FFB000]/40 bg-[#FFB000]/5 p-2">
                    <FIcon className="w-4 h-4 text-[#FFB000]" />
                  </span>
                  <h3 className="font-heading font-bold text-lg tracking-tight">{f.title}</h3>
                </div>
                <p className="text-sm text-zinc-400 leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Lead capture */}
      <section id="capture" className="border-t border-zinc-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          {explainerUrl && (
            <div className="mb-12" data-testid="industry-explainer-video">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// SEE DARA IN 90 SECONDS</div>
              <VideoExplainer videoUrl={explainerUrl} title={`DARA · ${cfg.label.split(' & ')[0]}`} />
            </div>
          )}
          {!explainerUrl && fallbackVideoUrl && (
            <div className="mb-12" data-testid="industry-fallback-video">
              <VideoAd
                src={fallbackVideoUrl}
                poster="/videos/dara_industrial_poster.jpg"
                label={cfg.defaultVideoLabel}
                caption={cfg.defaultVideoCaption}
                testId={`industry-${cfg.routeKey}-video`}
                maxHeight="70vh"
              />
            </div>
          )}
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-3">
            // {cfg.ctaLabel.toUpperCase()}
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">
            {cfg.formHeadline || "Let DARA show you the picture your current stack can't."}
          </h2>
          <p className="text-sm text-zinc-400 mt-3 max-w-xl">
            {cfg.formSub || `Drop your details and a DARA founder reaches out within one business day with a tailored ${cfg.label.split(" & ")[0]} walkthrough. No spam, no drip.`}
          </p>

          {!submitted && (
            <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="industry-lead-form">
              <Input label="Work email *" testid="lead-email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <Input label="Full name" testid="lead-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Input label="Company" testid="lead-company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
              <Input label="Role / title" testid="lead-role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} />
              <div className="sm:col-span-2">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">What problem are you solving? (optional)</span>
                  <textarea
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="lead-message"
                    className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                  />
                </label>
              </div>
              <div className="sm:col-span-2 flex items-center justify-between flex-wrap gap-3 pt-2">
                <p className="font-mono text-[10px] text-zinc-600 max-w-md">
                  By submitting you accept that DARA will contact you about this enquiry. We never share or sell your details.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="lead-submit"
                  className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90 disabled:opacity-40"
                >
                  {submitting ? <><Sparkles className="w-3.5 h-3.5 animate-pulse" /> SUBMITTING…</> : <><ChevronRight className="w-3.5 h-3.5" /> {cfg.ctaLabel}</>}
                </button>
              </div>
            </form>
          )}

          {submitted && (
            <div className="mt-8 border border-[#00E676]/40 bg-[#00E676]/5 p-6" data-testid="lead-success">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#00E676] mb-2">// LEAD CAPTURED</div>
              <h3 className="font-heading font-bold text-2xl tracking-tight">You're on the list.</h3>
              <p className="text-sm text-zinc-300 mt-2 max-w-xl leading-relaxed">
                A DARA founder will reach out within one business day with a tailored {cfg.label.split(" & ")[0]} walkthrough.
                In the meantime, you can <Link to="/founders-guide" className="text-[#FFB000] underline">read the Founder's Guide</Link> or{" "}
                <Link to="/signup" className="text-[#FFB000] underline">spin up a workspace</Link> immediately.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Footer cross-links */}
      <footer className="border-t border-zinc-900 bg-zinc-950/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 sm:grid-cols-4 gap-6">
          <FooterCol title="Industries" links={[
            { to: "/industries/mining", label: "Mining" },
            { to: "/industries/shipping", label: "Shipping" },
            { to: "/industries/heavy-machinery", label: "Heavy Machinery" },
            { to: "/industries/water", label: "Water & Utilities" },
          ]} />
          <FooterCol title="Product" links={[
            { to: "/founders-guide", label: "Founder's Guide" },
            { to: "/industrial", label: "Industrial Adapter" },
            { to: "/industrial/connector", label: "Design-Partner Pilot" },
          ]} />
          <FooterCol title="Account" links={[
            { to: "/login", label: "Sign in" },
            { to: "/signup", label: "Create workspace" },
          ]} />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">DARA OS</div>
            <p className="text-[11px] text-zinc-600 leading-relaxed">
              The Bloomberg-terminal for resource and industrial operators. Built in Australia, deployed worldwide.
            </p>
          </div>
        </div>
        <div className="border-t border-zinc-900 px-4 sm:px-6 py-4 max-w-7xl mx-auto flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">© DARA OS · {new Date().getFullYear()}</span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-700">DARA ENGINE · LIVE</span>
        </div>
      </footer>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required = false, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
      />
    </label>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">{title}</div>
      <ul className="space-y-2">
        {links.map(l => (
          <li key={l.to}>
            <Link to={l.to} className="text-[11px] text-zinc-400 hover:text-[#FFB000]">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

